import { FC, useCallback, useContext, useMemo } from "react";
import DataTable, { CellSize, DataTableProps } from "../table";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid";
import { Box, Chip, Tooltip } from "@mui/material";
import InputIcon from "@mui/icons-material/Input";
import { ModalContext } from "../../Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Modals/ModalConfirmAction/modal-confirm-action";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { useTranslation } from "react-i18next";

export interface LoadSequenceTableRow extends DbAssetObjectInfos {
  content: string;
}

export interface LoadSequenceTableProps extends Omit<DataTableProps, "columns"> {
  handleLoadFromAsset: (asset: DbAssetObjectInfos) => void;
  rows: LoadSequenceTableRow[];
}

const LoadSequenceTable: FC<LoadSequenceTableProps> = (props) => {
  const { t } = useTranslation();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const { rows, pageSize = 100 } = props;

  type Row = any;

  const handleLoadSequence = useCallback(
    (id: number) => {
      if (!openModal) return;

      openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
        onConfirm: () => {
          const sequenceToLoad = rows?.find((x) => x.id === id);
          if (!sequenceToLoad) return;
          props.handleLoadFromAsset(sequenceToLoad);
        },
        displayComment: false,
        title: t("modal:title.loseDataIfContinueConfirm"),
      });
    },
    [rows]
  );

  const cols: GridColDef[] = useMemo(
    () => [
      {
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        field: "name",
        headerName: t("common:name"),
        renderCell: (data: Row) => <Chip title={data.value} label={data.value || "-"} />,
      },
      {
        flex: CellSize.small,
        minWidth: CellSize.small,
        field: "content",
        headerName: t("common:content"),
        renderCell: (data: Row) => <Chip title={data.value} label={data.value || "-"} />,
      },
      {
        flex: CellSize.small,
        minWidth: CellSize.small,
        field: "actions",
        type: "actions",
        align: "right",
        getActions: (params: GridRowParams) => [
          <Tooltip title={t("tooltip:title.loadSequence")}>
            <GridActionsCellItem
              icon={<InputIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
              label={t("common:load")}
              onClick={(e) => handleLoadSequence(params?.row?.id)}
            />
          </Tooltip>,
        ],
      },
    ],
    [handleLoadSequence]
  );
  return (
    <Box width={"100%"}>
      <DataTable noControls pageSize={pageSize} rows={rows} isRowSelectable={() => false} columns={cols} />
    </Box>
  );
};

export default LoadSequenceTable;
